/* ==========================================================================
   Bản xem trước cho khối ```html / ```svg trong câu trả lời.

   Mã do mô hình viết (có thể chịu ảnh hưởng của trang web nó vừa đọc) nên
   KHÔNG BAO GIỜ chèn thẳng vào trang ERP. Nó chạy trong iframe:
     - sandbox="allow-scripts" KHÔNG có allow-same-origin → origin rỗng, không
       đọc được cookie, storage, DOM của ERP hay của widget;
     - không allow-forms / allow-popups / allow-top-navigation;
     - CSP riêng trong tài liệu: không fetch/XHR, không script ngoài, ảnh chỉ
       https: và data:.
   Chiều cao: script nhỏ trong iframe báo scrollHeight qua postMessage; widget
   chỉ nhận tin từ đúng contentWindow của iframe đó. ERP có CSP chặn script
   inline thì iframe giữ chiều cao mặc định và tự cuộn.
   ========================================================================== */

import { escapeHtml } from "../core/markdown.ts";

export const HTML_PENDING = `<div class="apx-chart apx-chart-pending">Đang dựng bản xem trước…</div>`;

const CSP =
  "default-src 'none'; style-src 'unsafe-inline'; img-src https: data:; font-src data:; script-src 'unsafe-inline'";

const BASE_CSS =
  "html,body{margin:0;background:#fff;color:#0f1115}" +
  "body{padding:12px;font:13px/1.5 system-ui,-apple-system,'Segoe UI',Roboto,'Noto Sans',sans-serif;overflow-wrap:anywhere}" +
  "img,svg{max-width:100%;height:auto}";

const FIT_SCRIPT =
  "(function(){var l=0;function s(){var h=Math.ceil(document.documentElement.scrollHeight);" +
  "if(h!==l){l=h;parent.postMessage({apxHtml:1,h:h},'*')}}" +
  "addEventListener('load',s);if(window.ResizeObserver)new ResizeObserver(s).observe(document.documentElement);s()})()";

/** Tài liệu đầy đủ cho srcdoc: khung + CSP của widget, nội dung của mô hình đặt trong body. */
export function frameDoc(code: string, lang: string): string {
  let inner = String(code || "");
  if (lang === "html" || lang === "htm") {
    /* bỏ vỏ tài liệu của mô hình để <meta CSP> của widget luôn đứng đầu <head> (meta CSP đặt sau thì vô hiệu) */
    inner = inner.replace(/<!doctype[^>]*>/gi, "").replace(/<\/?(html|head|body)\b[^>]*>/gi, "");
    inner = inner.replace(/<meta[^>]+http-equiv[^>]*>/gi, "");
  }
  return (
    '<!doctype html><html lang="vi"><head><meta charset="utf-8">' +
    '<meta http-equiv="Content-Security-Policy" content="' + CSP + '">' +
    '<meta name="viewport" content="width=device-width,initial-scale=1">' +
    "<style>" + BASE_CSS + "</style></head><body>" +
    inner +
    "<script>" + FIT_SCRIPT + "</script></body></html>"
  );
}

/** Khối xem trước. `live=false` (đang stream): chưa dựng iframe, để mỗi mẩu chữ mới không nạp lại trang. */
export function renderPreview(code: string, lang: string, live: boolean): string {
  const source =
    '<details class="apx-html-code"><summary>Xem mã ' + (lang === "svg" ? "SVG" : "HTML") + "</summary>" +
    '<pre class="apx-pre"><code>' + escapeHtml(code.replace(/\n$/, "")) + "</code></pre></details>";
  const frame = live
    ? '<iframe class="apx-html-frame" title="Bản xem trước" sandbox="allow-scripts" referrerpolicy="no-referrer" loading="lazy" srcdoc="' +
      escapeHtml(frameDoc(code, lang)) + '"></iframe>'
    : '<div class="apx-chart-pending">Bản xem trước hiện khi trả lời xong…</div>';
  return '<div class="apx-html">' + frame + source + "</div>";
}

/** Gắn một lần cho mỗi gốc (shadow root của widget / trang PDF): đặt chiều cao iframe theo tin nó gửi. */
export function listenFrameHeights(root: { querySelectorAll(sel: string): ArrayLike<Element> }): () => void {
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { apxHtml?: number; h?: number } | null;
    if (!data || data.apxHtml !== 1 || typeof data.h !== "number") return;
    const frames = root.querySelectorAll("iframe.apx-html-frame");
    for (let i = 0; i < frames.length; i++) {
      const f = frames[i] as HTMLIFrameElement;
      if (f.contentWindow === event.source) {
        f.style.height = Math.max(80, Math.min(2000, data.h)) + "px";
        return;
      }
    }
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

/* ---------------- bản in (PDF trên server) ----------------
   Trang in có CSP default-src 'none' (không script-src) → mọi script inline / on* bị chặn, mạng bị chặn
   trừ ảnh https; ở đây còn gỡ <script>, on*, iframe… cho chắc. Mã của mô hình không chạy gì trên server. Không cần iframe: nội dung đặt trong shadow root khai
   báo (<template shadowrootmode>) để CSS của mô hình không lan ra trang báo cáo, và cao đúng theo nội dung. */
const PRINT_CSS =
  ":host{display:block}.apx-doc{padding:12px;background:#fff;color:#0f1115;" +
  "font:12.5px/1.5 'Noto Sans','DejaVu Sans',Arial,sans-serif;overflow-wrap:anywhere}" +
  ".apx-doc img,.apx-doc svg{max-width:100%;height:auto}";

export function printableHtml(code: string): string {
  return String(code || "")
    .replace(/<script\b[\s\S]*?<\/script\s*>/gi, "")
    .replace(/<\/?(script|iframe|object|embed|link|base|meta|template|frame|frameset)\b[^>]*>/gi, "")
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<\/?(html|head|body)\b[^>]*>/gi, "")
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    /* selector html/body của mô hình → khung .apx-doc (trong shadow root không có html/body) */
    .replace(/<style\b([^>]*)>([\s\S]*?)<\/style>/gi, (_all, attrs: string, css: string) =>
      "<style" + attrs + ">" + css.replace(/(^|[\s,{}>+~])(html|body)(?=[\s,{.:#[>+~])/g, "$1.apx-doc") + "</style>",
    );
}

export function renderPrint(code: string): string {
  return (
    '<div class="apx-html apx-html-print"><template shadowrootmode="open"><style>' + PRINT_CSS + "</style>" +
    '<div class="apx-doc">' + printableHtml(code) + "</div></template></div>"
  );
}
